import { Property } from "../../../global/types";
import { MapBounds } from "./mapBoundsUtils";

export const GEO_CONFIG = {
  MIN_RADIUS_METERS: 500,
  MAX_RADIUS_METERS: 10000,
  RADIUS_STEP_METERS: 500,
  DEBOUNCE_MS: 800,
  MIN_MOVEMENT_METERS: 300,
  INITIAL_NEIGHBORHOOD_RADIUS: 2000,
  CENTER_PRECISION: 6,
} as const;

export const MIN_RADIUS_METERS = GEO_CONFIG.MIN_RADIUS_METERS;
export const MAX_RADIUS_METERS = GEO_CONFIG.MAX_RADIUS_METERS;
export const RADIUS_STEP_METERS = GEO_CONFIG.RADIUS_STEP_METERS;
export const MAP_GEO_DEBOUNCE_MS = GEO_CONFIG.DEBOUNCE_MS;
export const GEO_CENTER_PRECISION = GEO_CONFIG.CENTER_PRECISION;

export interface GeoMapState {
  center: { lat: number; lng: number };
  zoom: number;
  radius: number;
}

const FILTER_PARAM_KEYS = [
  "city",
  "UF",
  "uf",
  "condominium",
  "imovelType",
  "values",
  "area",
  "rooms",
  "garages",
  "bathrooms",
  "furnished",
  "total",
  "orderBy",
  "has_sea_view",
  "has_pool",
  "has_ac",
  "has_porch",
  "has_storage",
  "has_elevator",
  "has_gym",
  "has_sports",
  "has_playground",
  "has_bbq",
  "has_party",
  "has_game_room",
  "has_garden",
  "has_24h_concierge",
] as const;

const GEO_PARAM_KEYS = ["lat", "lng", "radius", "zoom", "neighborhood"] as const;

const EARTH_RADIUS_METERS = 6371e3;

export function calculateDistanceInMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

export function getBoundsCenter(bounds: MapBounds): { lat: number; lng: number } {
  return {
    lat: (bounds.NE.lat + bounds.SW.lat) / 2,
    lng: (bounds.NE.lng + bounds.SW.lng) / 2,
  };
}

export function filterPropertiesByRadius(
  properties: Property[],
  center: { lat: number; lng: number },
  radiusMeters: number
): Property[] {
  return properties.filter((property) => {
    const lat = parseFloat(property.address.latitude);
    const lng = parseFloat(property.address.longitude);

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return false;
    }

    return (
      calculateDistanceInMeters(center.lat, center.lng, lat, lng) <=
      radiusMeters
    );
  });
}

export function calculateRadiusFromBounds(bounds: MapBounds): number {
  const center = getBoundsCenter(bounds);
  const distance = calculateDistanceInMeters(
    center.lat,
    center.lng,
    bounds.NE.lat,
    bounds.NE.lng
  );

  let radius =
    Math.ceil((distance * 1.2) / GEO_CONFIG.RADIUS_STEP_METERS) *
    GEO_CONFIG.RADIUS_STEP_METERS;

  radius = Math.max(GEO_CONFIG.MIN_RADIUS_METERS, radius);
  radius = Math.min(GEO_CONFIG.MAX_RADIUS_METERS, radius);

  return radius;
}

export function isSignificantMovement(
  previousCenter: { lat: number; lng: number } | null,
  newCenter: { lat: number; lng: number }
): boolean {
  if (!previousCenter) {
    return true;
  }

  const distance = calculateDistanceInMeters(
    previousCenter.lat,
    previousCenter.lng,
    newCenter.lat,
    newCenter.lng
  );

  return distance >= GEO_CONFIG.MIN_MOVEMENT_METERS;
}

export function hasSignificantGeoChange(
  previous: GeoMapState | null,
  next: GeoMapState
): boolean {
  if (!previous) {
    return true;
  }

  const centerMoved = isSignificantMovement(previous.center, next.center);

  const radiusChanged =
    Math.abs(next.radius - previous.radius) >= GEO_CONFIG.RADIUS_STEP_METERS;

  const zoomChanged = previous.zoom !== next.zoom;

  return centerMoved || radiusChanged || zoomChanged;
}

export function calculateExpandedRadius(currentRadius: number): number {
  return Math.min(
    currentRadius + GEO_CONFIG.RADIUS_STEP_METERS,
    GEO_CONFIG.MAX_RADIUS_METERS
  );
}

export function clampRadius(radius: number): number {
  return Math.min(
    GEO_CONFIG.MAX_RADIUS_METERS,
    Math.max(GEO_CONFIG.MIN_RADIUS_METERS, radius)
  );
}

export function shouldActivateGeoSearch(
  visiblePropertiesCount: number,
  currentRadius: number
): boolean {
  return (
    visiblePropertiesCount === 0 &&
    currentRadius < GEO_CONFIG.MAX_RADIUS_METERS
  );
}

export function getCurrentFiltersFromUrl(
  search: string = typeof window !== "undefined" ? window.location.search : ""
): Record<string, string> {
  const params = new URLSearchParams(search);
  const filters: Record<string, string> = {};

  FILTER_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      filters[key] = value;
    }
  });

  return filters;
}

export function buildGeoSearchUrl(
  center: { lat: number; lng: number },
  radius: number,
  zoom: number,
  options: {
    preserveFilters?: boolean;
    removeNeighborhood?: boolean;
    baseSearch?: string;
  } = {}
): string {
  const {
    preserveFilters = true,
    removeNeighborhood = true,
    baseSearch = typeof window !== "undefined" ? window.location.search : "",
  } = options;

  const params = new URLSearchParams(baseSearch);

  params.set("lat", center.lat.toFixed(GEO_CONFIG.CENTER_PRECISION));
  params.set("lng", center.lng.toFixed(GEO_CONFIG.CENTER_PRECISION));
  params.set("radius", String(clampRadius(radius)));
  params.set("zoom", String(zoom));

  if (removeNeighborhood) {
    params.delete("neighborhood");
  }

  if (preserveFilters) {
    const preserved = getCurrentFiltersFromUrl(baseSearch);
    Object.entries(preserved).forEach(([key, value]) => {
      if (!(GEO_PARAM_KEYS as readonly string[]).includes(key)) {
        params.set(key, value);
      }
    });
  }

  return `?${params.toString()}`;
}

async function geocodeCachedQuery(
  cacheKey: string,
  query: string
): Promise<{ lat: number; lng: number } | null> {
  const expiryKey = `${cacheKey}_expiry`;

  try {
    const cached = localStorage.getItem(cacheKey);
    const expiry = localStorage.getItem(expiryKey);

    if (cached && expiry && Date.now() < parseInt(expiry, 10)) {
      return JSON.parse(cached) as { lat: number; lng: number };
    }

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        query
      )}&format=json&limit=1`
    );
    const data = (await response.json()) as Array<{ lat: string; lon: string }>;

    if (!data?.[0]) {
      return null;
    }

    const coords = {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
    };

    localStorage.setItem(cacheKey, JSON.stringify(coords));
    localStorage.setItem(
      expiryKey,
      String(Date.now() + 7 * 24 * 60 * 60 * 1000)
    );

    return coords;
  } catch {
    return null;
  }
}

export async function geocodeNeighborhood(
  neighborhood: string,
  city: string = "Fortaleza",
  uf: string = "CE"
): Promise<{ lat: number; lng: number } | null> {
  return geocodeCachedQuery(
    `geocode_${neighborhood}_${city}_${uf}`,
    `${neighborhood}, ${city}, ${uf}`
  );
}

export async function geocodeCity(
  city: string,
  uf?: string | null
): Promise<{ lat: number; lng: number } | null> {
  const region = uf?.trim() ? `${city}, ${uf.trim()}, Brazil` : `${city}, Brazil`;
  return geocodeCachedQuery(`geocode_city_${city}_${uf ?? ""}`, region);
}
